/**
 * Serials page — per-unit serial-number tracking (IMEI, MAC, VIN, etc.)
 *
 * Powers the full lifecycle for tenants who need per-physical-unit
 * identifiers on high-value SKUs: receive → allocate → ship → return.
 *
 * Enable per-SKU via the `serial_tracked` flag on the Inventory edit
 * dialog; toggle strict-scan on the pick flow with `serial_pick_strict`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Barcode, ArrowsCounterClockwise, Warning, Shield, ArrowUUpLeft, CheckCircle, X, DownloadSimple, Plus, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  listSerials, receiveSerials, allocateSerial, shipSerial,
  returnSerial, quarantineSerial, releaseSerial, getSkus, updateSku, importSerialsCsv,
  getSerial, createSerialPallet, palletQrStickersPdfUrl, listSerialPallets, dissolveSerialPallet,
} from '../lib/api';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../components/ui/sheet';
import HelpAnchor from '../components/HelpAnchor';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_META = {
  in_stock:    { label: 'In stock',    cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  allocated:   { label: 'Allocated',   cls: 'bg-sky-100 text-sky-800 border-sky-300' },
  shipped:     { label: 'Shipped',     cls: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
  returned:    { label: 'Returned',    cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  quarantined: { label: 'QA Hold',     cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  scrapped:    { label: 'Scrapped',    cls: 'bg-zinc-200 text-zinc-800 border-zinc-400' },
};

const STATUS_FILTER_OPTIONS = [
  { value: '__all', label: 'All statuses' },
  ...Object.entries(STATUS_META).map(([v, m]) => ({ value: v, label: m.label })),
];

export default function Serials() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('__all');
  const [search, setSearch] = useState('');
  const [skuFilter, setSkuFilter] = useState('');
  const [skus, setSkus] = useState([]);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [enableOpen, setEnableOpen] = useState(false);
  const [returnFor, setReturnFor] = useState(null);
  const [rmaNote, setRmaNote] = useState('');
  const [historyFor, setHistoryFor] = useState(null); // full serial doc for drawer
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [palletBusy, setPalletBusy] = useState(false);
  const [palletsOpen, setPalletsOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status && status !== '__all') params.status = status;
      if (search.trim()) params.serial_no = search.trim();
      if (skuFilter) params.sku_code = skuFilter;
      const r = await listSerials(params);
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load serials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [status, skuFilter]);
  useEffect(() => {
    // Debounce serial-search
    const t = setTimeout(refresh, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);
  useEffect(() => {
    getSkus({ limit: 500 }).then((r) => setSkus(Array.isArray(r.data) ? r.data : (r.data?.skus || []))).catch(() => {});
  }, []);

  const counts = useMemo(() => {
    const c = {};
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const doAction = async (label, fn, id) => {
    try {
      await fn(id);
      toast.success(label);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || `${label} failed`);
    }
  };

  const submitReturn = async () => {
    if (!rmaNote.trim()) { toast.error('RMA note required'); return; }
    try {
      await returnSerial(returnFor.id, { rma_note: rmaNote.trim() });
      toast.success('Serial marked returned');
      setReturnFor(null); setRmaNote('');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Return failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-4" data-testid="serials-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Barcode size={26} weight="fill" /> Serials / IMEI
            <HelpAnchor articleId="serials" label="How serial tracking works" />
          </h1>
          <p className="text-sm text-muted-foreground">
            Per-unit serial-number tracking for high-value SKUs. Enable on any SKU with the <code className="font-mono text-xs bg-muted px-1">serial_tracked</code> flag.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} data-testid="serials-refresh">
            <ArrowsCounterClockwise size={16} className="mr-1" weight="bold" /> Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const params = new URLSearchParams();
              if (status && status !== '__all') params.set('status', status);
              if (search.trim()) params.set('serial_no', search.trim());
              if (skuFilter) params.set('sku_code', skuFilter);
              const url = `${API}/api/serials/export.csv${params.toString() ? `?${params}` : ''}`;
              window.open(url, '_blank');
            }}
            data-testid="serials-open-export"
            title="Download the current filter set as CSV (auditors + warranty rego)"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEnableOpen(true)} data-testid="serials-open-enable">
            Enable on SKU
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPalletsOpen(true)} data-testid="serials-open-pallets">
            Pallets
          </Button>
          <label className="inline-flex items-center gap-1 px-3 py-2 border border-border bg-card hover:bg-accent text-xs font-black uppercase tracking-widest cursor-pointer" data-testid="serials-open-csv">
            <UploadSimple size={14} weight="bold" /> Import CSV
            <input
              type="file"
              accept=".csv,.tsv,.txt"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (!f) return;
                try {
                  const { data } = await importSerialsCsv(f);
                  toast.success(data.message || `Imported ${data.inserted}`);
                  if ((data.errors || []).length) {
                    const first = data.errors.slice(0, 3).map((x) => `row ${x.row}: ${x.error}`).join(' · ');
                    toast.warning(`${data.errors.length} rejected — ${first}`);
                  }
                  refresh();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Import failed');
                }
              }}
            />
          </label>
          <Button size="sm" onClick={() => setReceiveOpen(true)} data-testid="serials-open-receive">
            <Plus size={16} className="mr-1" weight="bold" /> Receive serials
          </Button>
        </div>
      </div>

      {/* Status counter strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="serials-counters">
        {Object.entries(STATUS_META).map(([key, meta]) => (
          <button
            key={key}
            onClick={() => setStatus(status === key ? '__all' : key)}
            className={`border p-3 text-left transition-colors ${status === key ? meta.cls : 'bg-card hover:bg-accent border-border'}`}
            data-testid={`serials-counter-${key}`}
          >
            <div className="text-xs font-black uppercase tracking-widest">{meta.label}</div>
            <div className="text-2xl font-black font-mono">{counts[key] || 0}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
        <Input
          placeholder="Search serial number… (partial match)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
          data-testid="serials-search"
        />
        <Select value={skuFilter || '__all_skus'} onValueChange={(v) => setSkuFilter(v === '__all_skus' ? '' : v)}>
          <SelectTrigger className="max-w-xs" data-testid="serials-sku-filter">
            <SelectValue placeholder="Filter by SKU" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all_skus">All SKUs</SelectItem>
            {skus.slice(0, 300).map((s) => (
              <SelectItem key={s.id} value={s.sku_code}>{s.sku_code} · {s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="max-w-xs" data-testid="serials-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTER_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-card border border-border overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground border-b border-border bg-muted/50">
          <div className="col-span-3">Serial</div>
          <div className="col-span-3">SKU</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Received / Order</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="serials-empty">
            No serials match the current filters.
            {status === '__all' && !search && !skuFilter && (
              <div className="mt-2">
                <Button size="sm" variant="outline" onClick={() => setReceiveOpen(true)}>Receive your first serial</Button>
              </div>
            )}
          </div>
        ) : (
          rows.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.in_stock;
            const isSelected = selectedIds.has(r.id);
            return (
              <div
                key={r.id}
                className={`grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-b-0 items-center text-sm hover:bg-accent/40 cursor-pointer ${isSelected ? 'bg-brand/5' : ''}`}
                data-testid={`serial-row-${r.serial_no}`}
                onClick={async (e) => {
                  // Ignore clicks on interactive descendants (checkboxes, action buttons)
                  const el = e.target;
                  if (el.closest && el.closest('button, input, a')) return;
                  setHistoryFor({ ...r, __partial: true });
                  setHistoryLoading(true);
                  try {
                    const resp = await getSerial(r.id);
                    setHistoryFor(resp.data);
                  } catch (err) {
                    toast.error('Failed to load history');
                    setHistoryFor(null);
                  } finally {
                    setHistoryLoading(false);
                  }
                }}
              >
                <div className="col-span-3 font-mono font-bold flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      const s = new Set(selectedIds);
                      if (e.target.checked) s.add(r.id); else s.delete(r.id);
                      setSelectedIds(s);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={r.status !== 'in_stock' || !!r.pallet_id}
                    className="flex-shrink-0"
                    data-testid={`serial-select-${r.serial_no}`}
                    title={r.pallet_id ? 'Already on a pallet' : r.status !== 'in_stock' ? 'Only in-stock serials can be palletised' : 'Select for pallet grouping'}
                  />
                  {r.serial_no}
                  {r.pallet_id && (
                    <span className="ml-1 px-1 py-0.5 border border-purple-400 bg-purple-50 text-purple-800 text-[9px] font-black uppercase tracking-widest" title={`On pallet ${r.pallet_id.slice(-6)}`}>
                      PAL
                    </span>
                  )}
                </div>
                <div className="col-span-3">
                  <div className="font-mono text-xs">{r.sku_code || '—'}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.sku_name}</div>
                </div>
                <div className="col-span-2">
                  <span className={`inline-block px-2 py-0.5 border text-[10px] font-black uppercase tracking-widest ${meta.cls}`}>
                    {meta.label}
                  </span>
                  {r.rma_note && (
                    <div className="text-[10px] text-amber-700 mt-1 truncate" title={r.rma_note}>RMA: {r.rma_note}</div>
                  )}
                </div>
                <div className="col-span-2 text-xs text-muted-foreground">
                  {r.received_at ? new Date(r.received_at).toLocaleDateString() : '—'}
                  {r.order_id && <div className="text-xs">Order {r.order_id.slice(-6)}</div>}
                </div>
                <div className="col-span-2 flex justify-end gap-1 flex-wrap">
                  {r.status === 'in_stock' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => doAction('Allocated', (id) => allocateSerial(id, {}), r.id)} data-testid={`serial-${r.serial_no}-allocate`}>
                        Allocate
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => doAction('Quarantined', (id) => quarantineSerial(id, { reason: 'Manual QA hold' }), r.id)} data-testid={`serial-${r.serial_no}-quarantine`}>
                        <Shield size={12} weight="bold" />
                      </Button>
                    </>
                  )}
                  {r.status === 'allocated' && (
                    <Button size="sm" onClick={() => doAction('Shipped', (id) => shipSerial(id, {}), r.id)} data-testid={`serial-${r.serial_no}-ship`}>
                      Ship
                    </Button>
                  )}
                  {r.status === 'shipped' && (
                    <Button size="sm" variant="outline" onClick={() => { setReturnFor(r); setRmaNote(''); }} data-testid={`serial-${r.serial_no}-return`}>
                      <ArrowUUpLeft size={12} weight="bold" className="mr-1" /> Return
                    </Button>
                  )}
                  {r.status === 'quarantined' && (
                    <Button size="sm" variant="outline" onClick={() => doAction('Released', (id) => releaseSerial(id), r.id)} data-testid={`serial-${r.serial_no}-release`}>
                      <CheckCircle size={12} weight="bold" className="mr-1" /> Release
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <ReceiveDialog
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        onDone={() => { setReceiveOpen(false); refresh(); }}
        skus={skus}
      />

      <EnableDialog
        open={enableOpen}
        onClose={() => setEnableOpen(false)}
        onDone={() => { setEnableOpen(false); getSkus({ limit: 500 }).then((r) => setSkus(Array.isArray(r.data) ? r.data : (r.data?.skus || []))).catch(() => {}); }}
        skus={skus}
      />

      {/* RMA dialog */}
      <Dialog open={!!returnFor} onOpenChange={(o) => !o && setReturnFor(null)}>
        <DialogContent data-testid="serials-return-dialog">
          <DialogHeader>
            <DialogTitle>Return serial {returnFor?.serial_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="text-muted-foreground">
              Marking this serial as returned brings it back into stock with an RMA note attached. The reason is required for warranty audit trails.
            </div>
            <label className="block text-xs font-black uppercase tracking-widest text-muted-foreground">RMA note</label>
            <Textarea
              value={rmaNote}
              onChange={(e) => setRmaNote(e.target.value)}
              rows={3}
              placeholder="e.g. Customer returned — screen defect. RMA-2026-0142"
              data-testid="serials-return-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnFor(null)}>Cancel</Button>
            <Button onClick={submitReturn} data-testid="serials-return-submit">Mark returned</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pallet grouping — sticky bulk action bar when serials are selected */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-purple-950 text-white border-2 border-purple-500 px-4 py-3 shadow-2xl" data-testid="serials-pallet-bar">
          <span className="text-xs font-black uppercase tracking-widest">
            {selectedIds.size} selected
          </span>
          <Button
            size="sm"
            className="bg-purple-500 hover:bg-purple-400 text-white"
            disabled={palletBusy}
            data-testid="serials-pallet-group-btn"
            onClick={async () => {
              setPalletBusy(true);
              try {
                const r = await createSerialPallet({ serial_ids: Array.from(selectedIds) });
                toast.success(`Pallet ${r.data.pallet_code} created (${selectedIds.size} serials)`);
                setSelectedIds(new Set());
                refresh();
              } catch (e) {
                toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Group failed');
              } finally {
                setPalletBusy(false);
              }
            }}
          >
            {palletBusy ? 'Grouping…' : 'Group into pallet'}
          </Button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="text-xs opacity-70 hover:opacity-100"
            data-testid="serials-pallet-cancel"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Pallet manager dialog — list + QR sticker print + dissolve */}
      <PalletManagerDialog open={palletsOpen} onClose={() => { setPalletsOpen(false); refresh(); }} />

      {/* Serial history drawer */}
      <Sheet open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="serials-history-drawer">
          <SheetHeader>
            <SheetTitle className="font-mono">{historyFor?.serial_no || '…'}</SheetTitle>
          </SheetHeader>
          {historyFor && (
            <div className="mt-4 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">STATUS</div>
                  <div className={`inline-block mt-1 px-2 py-0.5 border text-[10px] font-black uppercase tracking-widest ${(STATUS_META[historyFor.status] || STATUS_META.in_stock).cls}`}>
                    {(STATUS_META[historyFor.status] || STATUS_META.in_stock).label}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">SKU</div>
                  <div className="font-mono">{historyFor.sku_code}</div>
                  <div className="text-muted-foreground text-xs truncate">{historyFor.sku_name}</div>
                </div>
                {historyFor.order_id && (
                  <div className="col-span-2">
                    <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">ORDER</div>
                    <div className="font-mono text-xs">{historyFor.order_id}</div>
                  </div>
                )}
                {historyFor.rma_note && (
                  <div className="col-span-2">
                    <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">RMA NOTE</div>
                    <div className="p-2 border border-amber-300 bg-amber-50 text-xs">{historyFor.rma_note}</div>
                  </div>
                )}
                {historyFor.pallet_id && (
                  <div className="col-span-2">
                    <div className="text-[10px] font-black uppercase tracking-widest text-purple-700">PALLET</div>
                    <div className="font-mono text-xs">{historyFor.pallet_id}</div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">
                  Lifecycle audit trail
                </div>
                {historyLoading && <div className="text-xs text-muted-foreground">Loading…</div>}
                {(historyFor.history || []).length === 0 && !historyLoading && (
                  <div className="text-xs text-muted-foreground">No events recorded.</div>
                )}
                <ol className="border-l-2 border-brand pl-4 space-y-3">
                  {(historyFor.history || []).map((h, i) => (
                    <li key={i} className="relative" data-testid={`serials-history-event-${i}`}>
                      <span className="absolute -left-[22px] top-1 w-3 h-3 rounded-full bg-brand" />
                      <div className="text-xs font-black uppercase tracking-widest text-brand">{h.event}</div>
                      <div className="text-xs text-muted-foreground">
                        {h.at ? new Date(h.at).toLocaleString() : '—'}
                        {h.by_email && <> · <span className="font-mono">{h.by_email}</span></>}
                      </div>
                      {h.note && <div className="text-xs mt-0.5">{h.note}</div>}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}


function ReceiveDialog({ open, onClose, onDone, skus }) {
  const [skuId, setSkuId] = useState('');
  const [variantId, setVariantId] = useState('');
  const [variants, setVariants] = useState([]);
  const [mode, setMode] = useState('paste'); // paste | auto
  const [pasted, setPasted] = useState('');
  const [autoCount, setAutoCount] = useState(10);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setSkuId(''); setVariantId(''); setVariants([]);
    setPasted(''); setAutoCount(10); setMode('paste');
  }, [open]);

  useEffect(() => {
    if (!skuId) { setVariants([]); setVariantId(''); return; }
    const sku = skus.find((s) => s.id === skuId);
    const vs = sku?.variants || [];
    setVariants(vs);
    if (vs.length === 1) setVariantId(vs[0].id);
  }, [skuId, skus]);

  const submit = async () => {
    if (!variantId) { toast.error('Pick a SKU + variant first'); return; }
    const body = { variant_id: variantId };
    if (mode === 'auto') {
      body.auto_generate = Number(autoCount) || 0;
      if (body.auto_generate < 1) { toast.error('auto_generate must be at least 1'); return; }
    } else {
      const parsed = pasted.split(/[\n,;\t]+/).map((s) => s.trim()).filter(Boolean);
      if (!parsed.length) { toast.error('Paste at least one serial'); return; }
      body.serials = parsed;
    }
    setSubmitting(true);
    try {
      const r = await receiveSerials(body);
      toast.success(`Received ${r.data.count} serial${r.data.count === 1 ? '' : 's'}`);
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === 'duplicate_serial') {
        toast.error(`Duplicates already exist: ${(detail.duplicates || []).slice(0, 5).join(', ')}`);
      } else {
        toast.error(detail?.message || detail || 'Receive failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="serials-receive-dialog">
        <DialogHeader>
          <DialogTitle>Receive serials</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">SKU</label>
            <Select value={skuId} onValueChange={setSkuId}>
              <SelectTrigger data-testid="serials-receive-sku"><SelectValue placeholder="Choose a SKU" /></SelectTrigger>
              <SelectContent>
                {skus.slice(0, 300).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.sku_code} · {s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {variants.length > 1 && (
            <div>
              <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">Variant</label>
              <Select value={variantId} onValueChange={setVariantId}>
                <SelectTrigger data-testid="serials-receive-variant"><SelectValue placeholder="Choose a variant" /></SelectTrigger>
                <SelectContent>
                  {variants.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.variant_code || v.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('paste')}
              className={`flex-1 py-2 border text-xs font-black uppercase tracking-widest ${mode === 'paste' ? 'bg-brand text-white border-brand' : 'bg-card border-border'}`}
              data-testid="serials-mode-paste"
            >
              Paste / scan
            </button>
            <button
              type="button"
              onClick={() => setMode('auto')}
              className={`flex-1 py-2 border text-xs font-black uppercase tracking-widest ${mode === 'auto' ? 'bg-brand text-white border-brand' : 'bg-card border-border'}`}
              data-testid="serials-mode-auto"
            >
              Auto-generate
            </button>
          </div>
          {mode === 'paste' ? (
            <div>
              <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                Serials (one per line — or comma-separated)
              </label>
              <Textarea
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                rows={6}
                placeholder={"IMEI-123456789012345\nIMEI-123456789012346\n…"}
                className="font-mono text-xs"
                data-testid="serials-receive-paste"
              />
              <div className="text-xs text-muted-foreground mt-1">
                Non-empty lines: <span className="font-bold">{pasted.split(/[\n,;\t]+/).map((s) => s.trim()).filter(Boolean).length}</span>
              </div>
            </div>
          ) : (
            <div>
              <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">How many to mint?</label>
              <Input
                type="number"
                min={1}
                max={10000}
                value={autoCount}
                onChange={(e) => setAutoCount(e.target.value)}
                data-testid="serials-receive-auto-count"
              />
              <div className="text-xs text-muted-foreground mt-1">
                We'll mint <span className="font-bold">{autoCount || 0}</span> serials in the form <code className="font-mono">SN-XXXXXXXX</code>.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="serials-receive-submit">
            {submitting ? 'Receiving…' : 'Receive'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EnableDialog({ open, onClose, onDone, skus }) {
  const [skuId, setSkuId] = useState('');
  const [tracked, setTracked] = useState(true);
  const [strict, setStrict] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setSkuId(''); setTracked(true); setStrict(false); }
  }, [open]);

  useEffect(() => {
    if (!skuId) return;
    const sku = skus.find((s) => s.id === skuId);
    setTracked(!!sku?.serial_tracked);
    setStrict(!!sku?.serial_pick_strict);
  }, [skuId, skus]);

  const submit = async () => {
    if (!skuId) { toast.error('Pick a SKU first'); return; }
    setSaving(true);
    try {
      await updateSku(skuId, { serial_tracked: !!tracked, serial_pick_strict: !!strict });
      toast.success('Serial tracking updated');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="serials-enable-dialog">
        <DialogHeader>
          <DialogTitle>Enable serial tracking on a SKU</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">SKU</label>
            <Select value={skuId} onValueChange={setSkuId}>
              <SelectTrigger data-testid="serials-enable-sku">
                <SelectValue placeholder="Choose a SKU" />
              </SelectTrigger>
              <SelectContent>
                {skus.slice(0, 500).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.sku_code} · {s.name}
                    {s.serial_tracked ? ' · tracked' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-3 p-3 border border-border cursor-pointer hover:bg-accent">
            <input
              type="checkbox"
              checked={tracked}
              onChange={(e) => setTracked(e.target.checked)}
              className="mt-1"
              data-testid="serials-enable-tracked"
            />
            <div>
              <div className="font-bold">Serial-tracked</div>
              <div className="text-xs text-muted-foreground">Every receipt must record serials; every dispatch attaches them to the shipment.</div>
            </div>
          </label>
          <label className={`flex items-start gap-3 p-3 border border-border cursor-pointer hover:bg-accent ${!tracked ? 'opacity-50' : ''}`}>
            <input
              type="checkbox"
              checked={strict}
              onChange={(e) => setStrict(e.target.checked)}
              disabled={!tracked}
              className="mt-1"
              data-testid="serials-enable-strict"
            />
            <div>
              <div className="font-bold">Strict pick scan</div>
              <div className="text-xs text-muted-foreground">Pickers must scan the exact serial being taken. Off = system auto-assigns the oldest in-stock serial (FIFO).</div>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="serials-enable-submit">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PalletManagerDialog({ open, onClose }) {
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await listSerialPallets({ limit: 200 });
      setPallets(r.data || []);
    } catch (e) {
      toast.error('Failed to load pallets');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const doDissolve = async (p) => {
    if (!window.confirm(`Dissolve pallet ${p.pallet_code}? Its ${p.serial_count} serials will keep their location but lose the pallet link.`)) return;
    try {
      const r = await dissolveSerialPallet(p.id);
      toast.success(`Pallet ${p.pallet_code} dissolved — ${r.data.serials_freed} serials freed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dissolve failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="serials-pallets-dialog">
        <DialogHeader>
          <DialogTitle>Serial Pallets</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground">
          Every pallet groups N in-stock serials so warehouse transfers can move them in one scan. Print QR stickers
          to slap on the pallet, or dissolve to ungroup.
        </div>
        <div className="max-h-[60vh] overflow-y-auto border border-border">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground border-b border-border bg-muted/50">
            <div className="col-span-3">Pallet</div>
            <div className="col-span-2">Serials</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Warehouse</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : pallets.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="pallets-empty">
              No pallets yet. Select in-stock serials on the Serials page and click "Group into pallet".
            </div>
          ) : (
            pallets.map((p) => (
              <div key={p.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-b-0 items-center text-sm hover:bg-accent/40" data-testid={`pallet-row-${p.pallet_code}`}>
                <div className="col-span-3 font-mono font-bold">{p.pallet_code}</div>
                <div className="col-span-2 font-mono">{p.serial_count || 0}</div>
                <div className="col-span-2">
                  <span className={`inline-block px-2 py-0.5 border text-[10px] font-black uppercase tracking-widest ${p.status === 'dissolved' ? 'bg-zinc-100 text-zinc-700 border-zinc-300' : 'bg-purple-100 text-purple-800 border-purple-300'}`}>
                    {p.status || 'in_stock'}
                  </span>
                </div>
                <div className="col-span-2 text-xs text-muted-foreground truncate">
                  {p.warehouse_id || '—'}
                </div>
                <div className="col-span-3 flex justify-end gap-1">
                  <a
                    href={palletQrStickersPdfUrl(p.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 border border-border hover:bg-accent text-xs"
                    data-testid={`pallet-qr-${p.pallet_code}`}
                    title="Print A4 grid of QR stickers"
                  >
                    QR
                  </a>
                  {p.status !== 'dissolved' && (
                    <Button size="sm" variant="outline" onClick={() => doDissolve(p)} data-testid={`pallet-dissolve-${p.pallet_code}`}>
                      Dissolve
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

